/**
 * Utilitário para impressão customizada de relatórios
 * Inclui cabeçalho com logo, título e dados do usuário
 * Rodapé com razão social e CNPJ da empresa
 */

import { Config, getToken, getUser } from '../services/config';

interface EmpresaData {
  razao_social: string;
  cnpj: string;
  logomarca_base64?: string;
  logomarca_url?: string;
}

/**
 * Busca os dados da empresa do usuário logado para impressão
 * Usa endpoint específico que inclui logomarca_base64
 */
const fetchEmpresaData = async (): Promise<EmpresaData | null> => {
  try {
    const token = getToken();
    if (!token) {
      console.error('❌ Token não encontrado');
      return null;
    }

    // Usa endpoint específico para impressão que inclui logomarca_base64
    const response = await fetch(`${Config.API_BASE_URL}/empresas/dados-impressao`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ Dados da empresa para impressão recebidos:', {
        tem_razao_social: !!result.data?.razao_social,
        tem_cnpj: !!result.data?.cnpj,
        tem_logo_base64: !!result.data?.logomarca_base64,
        tamanho_logo: result.data?.logomarca_base64?.length || 0
      });
      // A API retorna { success: true, data: {...} }
      return result.data || null;
    } else {
      console.error('❌ Erro ao buscar dados da empresa - Status:', response.status);
      const errorText = await response.text();
      console.error('❌ Detalhes do erro:', errorText);
      return null;
    }
  } catch (error) {
    console.error('❌ Erro ao buscar dados da empresa:', error);
    return null;
  }
};

/**
 * Formata CNPJ no padrão brasileiro
 */
const formatarCNPJ = (cnpj: string): string => {
  if (!cnpj) return 'N/A';
  const cleaned = String(cnpj).replace(/\D/g, '');
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

/**
 * Função principal de impressão customizada
 */
export const printReport = async () => {
  // Obtém o título do relatório do h1 dentro de page-header (não do header principal)
  const headerLeft = document.querySelector('.page-container .page-header .header-left h1');
  const reportTitle = headerLeft?.textContent?.trim() || 'Relatório';

  console.log('📄 Título do relatório detectado:', reportTitle);

  // Obtém os elementos que devem ser impressos
  const statisticsElement = document.querySelector('.report-statistics');
  const tableContainerElement = document.querySelector('.report-table-container');

  if (!statisticsElement && !tableContainerElement) {
    console.error('Elementos de relatório não encontrados');
    alert('Não foi possível encontrar os dados do relatório para impressão');
    return;
  }

  // Busca dados do usuário logado
  const user = getUser();
  const usuarioNome = user?.nome || 'Usuário';

  // Busca dados da empresa
  console.log('🔍 Buscando dados da empresa...');
  const empresaData = await fetchEmpresaData();
  console.log('📊 Dados da empresa processados:', {
    razao_social: empresaData?.razao_social,
    cnpj: empresaData?.cnpj,
    tem_logo: !!empresaData?.logomarca_base64
  });

  const razaoSocial = empresaData?.razao_social || 'N/A';
  const cnpj = empresaData?.cnpj ? formatarCNPJ(empresaData.cnpj) : 'N/A';

  // Lógica de logo idêntica ao PDF:
  // 1. Tenta usar logomarca_base64 da empresa (se existir)
  // 2. Se não existir, usa logo padrão do sistema
  const logoSrc = empresaData?.logomarca_base64
    ? empresaData.logomarca_base64
    : '/images/logo-confereai.png';

  console.log('🖨️ Preparando impressão:', { razaoSocial, cnpj, logoSrc: logoSrc.substring(0, 50) + '...' });

  // Clona os elementos para não afetar a página original
  const statisticsClone = statisticsElement?.cloneNode(true) as HTMLElement;
  const tableContainerClone = tableContainerElement?.cloneNode(true) as HTMLElement;

  // Data e hora atual
  const agora = new Date();
  const dataFormatada = agora.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const horaFormatada = agora.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Cria uma janela de impressão
  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    console.error('Não foi possível abrir janela de impressão');
    alert('Não foi possível abrir a janela de impressão. Verifique se pop-ups estão permitidos.');
    return;
  }

  // Constrói o HTML da página de impressão
  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>${reportTitle}</title>
        <!-- Font Awesome para ícones -->
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" integrity="sha512-iecdLmaskl7CVkqkXNQ/ZH/XLlvWZOJyj7Yy7tcenmpD1ypASozpmT/E0iPtmFIB46ZmdtAc9eNBvH0H/ZpiBw==" crossorigin="anonymous" referrerpolicy="no-referrer" />
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          @page {
            size: A4 landscape;
            margin: 1.5cm 1cm;
          }

          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: #333;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* ==================== CABEÇALHO ==================== */
          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 1.5rem;
            padding-bottom: 1rem;
            border-bottom: 2px solid #04364F;
          }

          .print-header-left {
            flex: 0 0 120px;
            display: flex;
            align-items: center;
          }

          .print-header-left img {
            max-width: 100px;
            max-height: 60px;
            object-fit: contain;
          }

          .print-header-center {
            flex: 1;
            text-align: center;
            padding: 0 20px;
          }

          .print-header-center h1 {
            color: #04364F;
            font-size: 1.5rem;
            font-weight: 700;
            margin: 0;
          }

          .print-header-right {
            flex: 0 0 200px;
            text-align: right;
          }

          .print-header-right p {
            color: #666;
            font-size: 0.8rem;
            margin: 0.2rem 0;
            line-height: 1.4;
          }

          .print-header-right strong {
            color: #04364F;
            font-weight: 600;
          }

          /* ==================== ESTATÍSTICAS ==================== */
          .report-statistics {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 1rem;
            margin-bottom: 1.5rem;
          }

          .stat-card {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 1rem;
            display: flex;
            align-items: center;
            gap: 1rem;
          }

          .stat-icon,
          .stat-icon-primary,
          .stat-icon-success,
          .stat-icon-warning,
          .stat-icon-danger,
          .stat-icon-info {
            width: 48px;
            height: 48px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            flex-shrink: 0;
          }

          .stat-icon-primary {
            background: rgba(52, 152, 219, 0.1);
            color: #3498db;
          }

          .stat-icon-success {
            background: rgba(46, 204, 113, 0.1);
            color: #2ecc71;
          }

          .stat-icon-warning {
            background: rgba(230, 126, 34, 0.1);
            color: #e67e22;
          }

          .stat-icon-danger {
            background: rgba(231, 76, 60, 0.1);
            color: #e74c3c;
          }

          .stat-icon-info {
            background: rgba(52, 152, 219, 0.1);
            color: #3498db;
          }

          .stat-content h4 {
            color: #666;
            font-size: 0.75rem;
            font-weight: 600;
            text-transform: uppercase;
            margin-bottom: 0.25rem;
          }

          .stat-content .stat-value {
            color: #04364F;
            font-size: 1.5rem;
            font-weight: 700;
            margin: 0;
          }

          /* ==================== TABELA ==================== */
          .report-table-container {
            width: 100%;
            overflow: visible;
          }

          .report-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.75rem;
          }

          .report-table thead {
            background: linear-gradient(135deg, #04364F 0%, #032840 100%);
            color: white;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .report-table th {
            padding: 0.75rem 0.5rem;
            text-align: left;
            font-weight: 600;
            border: 1px solid #04364F;
          }

          .report-table td {
            padding: 0.625rem 0.5rem;
            border: 1px solid #e0e0e0;
          }

          .report-table tbody tr:nth-child(even) {
            background-color: #f8f9fa;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Badges */
          .badge {
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-size: 0.7rem;
            font-weight: 600;
            display: inline-block;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .badge-success {
            background-color: #d4edda;
            color: #155724;
          }

          .badge-warning {
            background-color: #fff3cd;
            color: #856404;
          }

          .badge-danger {
            background-color: #f8d7da;
            color: #721c24;
          }

          .badge-secondary {
            background-color: #e2e3e5;
            color: #383d41;
          }

          .badge-info {
            background-color: #d1ecf1;
            color: #0c5460;
          }

          .badge-primary {
            background-color: #cfe2ff;
            color: #084298;
          }

          /* Progress bars */
          .progress-bar-container {
            position: relative;
            width: 100%;
            height: 20px;
            background-color: #e0e0e0;
            border-radius: 4px;
            overflow: hidden;
          }

          .progress-bar-fill {
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            background: linear-gradient(90deg, #2ecc71 0%, #27ae60 100%);
            transition: width 0.3s;
          }

          .progress-bar-text {
            position: absolute;
            width: 100%;
            text-align: center;
            line-height: 20px;
            font-size: 0.7rem;
            font-weight: 600;
            color: #333;
            z-index: 1;
          }

          /* ==================== RODAPÉ ==================== */
          .print-footer {
            margin-top: 3rem;
            text-align: center;
            font-size: 0.75rem;
            color: #666;
            page-break-inside: avoid;
          }

          .print-footer p {
            margin: 0.25rem 0;
          }

          .print-footer strong {
            color: #04364F;
            font-weight: 600;
          }

          /* ==================== PRINT MEDIA ==================== */
          @media print {
            body {
              margin: 0;
            }

            .report-table {
              page-break-inside: auto;
            }

            .report-table tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }

            .report-table thead {
              display: table-header-group;
            }

            .print-footer {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
            }
          }

          /* Ocultar elementos não necessários */
          .action-btn,
          .actions-cell button,
          .report-actions,
          .report-table-header,
          .results-counter {
            display: none !important;
          }
        </style>
      </head>
      <body>
        <!-- CABEÇALHO -->
        <div class="print-header">
          <div class="print-header-left">
            <img src="${logoSrc}" alt="Logo" onerror="this.style.display='none'" />
          </div>
          <div class="print-header-center">
            <h1>${reportTitle}</h1>
          </div>
          <div class="print-header-right">
            <p><strong>Usuário:</strong> ${usuarioNome}</p>
            <p><strong>Data:</strong> ${dataFormatada}</p>
            <p><strong>Hora:</strong> ${horaFormatada}</p>
          </div>
        </div>

        <!-- ESTATÍSTICAS -->
        ${statisticsClone ? statisticsClone.outerHTML : ''}

        <!-- TABELA DE DADOS -->
        ${tableContainerClone ? tableContainerClone.outerHTML : ''}

        <!-- RODAPÉ -->
        <div class="print-footer">
          <p><strong>${razaoSocial}</strong> | CNPJ: ${cnpj}</p>
        </div>
      </body>
    </html>
  `;

  // Escreve o conteúdo na janela de impressão
  printWindow.document.write(printContent);
  printWindow.document.close();

  // Aguarda o carregamento completo antes de imprimir
  printWindow.onload = () => {
    // Pequeno delay para garantir que os ícones do Font Awesome carregaram
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      // Não fecha automaticamente para o usuário poder revisar
      // printWindow.close();
    }, 500);
  };
};
